import {Component, OnInit} from '@angular/core';
import {CacheJsService, ConfiguratorI18nCacheService, SelectItem, ConfiguratorI18nService, FsTranslate} from '@tj/tj-dmp-lib-api';
import {AbstractEditorComponent} from '../abstract-editor.component';
import {AbstractControl, FormBuilder, ValidationErrors, ValidatorFn } from '@angular/forms';
import {ValidatorEditorService} from "../validator-editor.service";

function extractDialCode(codeValue: any): string | null {
    if (!codeValue) {
        return null;
    }

    if (typeof codeValue === 'string') {
        const match = codeValue.match(/\+\d+/);
        return match ? match[0] : null;
    }

    if (typeof codeValue === 'object') {
        if (codeValue.dial_code && typeof codeValue.dial_code === 'string') {
            return codeValue.dial_code.replace(/\s/g, '');
        }

        if (codeValue.label && typeof codeValue.label === 'string') {
            const match = codeValue.label.match(/\+\d+/);
            return match ? match[0] : null;
        }
    }

    return null;
}

function hasCountrySelection(codeValue: any): boolean {
    if (!codeValue) {
        return false;
    }
    if (typeof codeValue === 'string') {
        return codeValue.trim().length > 0;
    }
    if (typeof codeValue === 'object') {
        return Boolean(codeValue.value || codeValue.label || codeValue.dial_code);
    }
    return false;
}

function parseStoredPhone(rawValue: string, availableDialCodes: string[]): { dialCode: string | null; number: string } {
    if (!rawValue) {
        return { dialCode: null, number: '' };
    }

    const compactValue = rawValue.replace(/\s/g, '');

    if (!compactValue.includes('+')) {
        return { dialCode: null, number: compactValue.replace(/\D/g, '') };
    }

    if (compactValue.includes('-')) {
        const [codePart, numberPart] = compactValue.split('-', 2);
        return {
            dialCode: codePart || null,
            number: (numberPart || '').replace(/\D/g, '')
        };
    }

    const normalized = compactValue.replace(/[^\d+]/g, '');
    const dialCodes = [...availableDialCodes].sort((a, b) => b.length - a.length);
    const matchedDialCode = dialCodes.find((dialCode) => normalized.startsWith(dialCode)) || null;

    if (!matchedDialCode) {
        return { dialCode: null, number: normalized.replace(/\D/g, '') };
    }

    return {
        dialCode: matchedDialCode,
        number: normalized.slice(matchedDialCode.length).replace(/\D/g, '')
    };
}

export function bothFieldsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const codeValue = control.get('code')?.value;
        const numTelValue = control.get('numTel')?.value;
        const countryDialCode = extractDialCode(codeValue);

        const isCodeEmpty = !hasCountrySelection(codeValue);
        const isNumTelEmpty = !numTelValue || numTelValue === '';

        if (isCodeEmpty && !isNumTelEmpty) {
            return { incompleteCode: true };
        }

        if (!isCodeEmpty && isNumTelEmpty) {
            return { incompleteTel: true };
        }

        if(!isCodeEmpty && !isNumTelEmpty){
            const phoneNumberRegex = /^\d+$/;
            if(!phoneNumberRegex.test(numTelValue)){
                return { invalidPhoneFormat : true }
            }

            const countryCodeDigits = countryDialCode ? countryDialCode.replace('+', '').length : 0;
            const maxNumTelDigits = 15- countryCodeDigits;
            if(numTelValue.length < 4 || numTelValue.length > maxNumTelDigits){
                return {invalidPhoneLength : true}
            }
        }

        return null;
    }
}

@Component({
    selector: 'dmp-phone-editor',
    templateUrl: './phone-editor.component.html',
    styleUrls: ['./phone-editor.component.scss', '../editor.component.scss'],
    standalone: false
})
export class PhoneEditorComponent extends AbstractEditorComponent implements OnInit {

    lang = 'FR';

    countries: Array<any> = [];
    country;
    /**
     * liste des items de la combo
     */
    items: SelectItem[];
    selected = 'FR';
    filteredCountries: any[];
    valueInput: string;

    defaultValue: SelectItem | null = null;
    displayPhone: string | null;

    private canvas: any = null;
    private readonly separatorItem: SelectItem = { label: '__SEPARATOR__', value: '__SEPARATOR__' };

    scrennIsLoaded = false;

    constructor(
        private readonly cacheJsService: CacheJsService,
        private readonly configuratorI18nCacheService: ConfiguratorI18nCacheService,
        private readonly configuratorI18nService: ConfiguratorI18nService,
        protected fb: FormBuilder,
        protected validatorService: ValidatorEditorService) {
        super(fb, validatorService);
    }
    
    transformBeforeSave(value: any) {
        return value;
    }

    ngOnInit(): void {
        this.init();
        this.updateCmp();
    }

    private resolveDialCodeFromSelection(codeValue: any): string | null {
        const directDialCode = extractDialCode(codeValue);
        if (directDialCode) {
            return directDialCode;
        }

        const selectionKey = typeof codeValue === 'string'
            ? codeValue.trim()
            : (codeValue && typeof codeValue === 'object' ? String(codeValue.value || '').trim() : '');

        if (!selectionKey) {
            return null;
        }

        const matchingItem = (this.items || []).find(item => String(item.value || '').trim() === selectionKey);
        return matchingItem ? extractDialCode(matchingItem) : null;
    }

    private updateCmp() {
        this.formGroup.addControl('new', this.fb.group({
            code: [null, []],
            numTel: ['', []]
        }, { validators: bothFieldsValidator() }));

        if (this.countries.length === 0) {
            this.items = [];
            this.configuratorI18nService
                .getListDialCode1()
                .subscribe((res: FsTranslate[]) => {
                    this.countries = res.filter(i => i.dial_code);
                    this.countries.forEach(c => c.dial_code = c.dial_code.replace(/\s/g, ''));

                    const dialCodeToLabel = new Map<string, string>();
                    const dialCodeToCountryCode = new Map<string, string>();

                    this.countries.forEach((country) => {
                        const c = country as any;
                        if (!dialCodeToLabel.has(c.dial_code)) {
                            dialCodeToLabel.set(c.dial_code, c.value);
                            dialCodeToCountryCode.set(c.dial_code, c.code);
                        }
                    });

                    this.items = Array.from(dialCodeToLabel.entries()).map(([dialCode, value]) => ({
                        label: `${value} : ${dialCode}`,
                        title: dialCode,
                        value: dialCodeToCountryCode.get(dialCode)
                    }));

                    this.items.sort((a, b) => a.label.localeCompare(b.label));
                    this.filteredCountries = this.buildSuggestions(this.items);
                    this.initValue(this.originalValue);
                    this.scrennIsLoaded = true;
                });
        }
    }

    private buildSuggestions(baseItems: SelectItem[]): SelectItem[] {
        const franceItem = baseItems.find(item => item.value === 'FR');
        if (!franceItem) {
            return [...baseItems];
        }

        return [franceItem, this.separatorItem, ...baseItems];
    }

    /**
     * initialisation de l'affichage des composants
     * @param value string
     */
    initValue(value: string) {
        const availableDialCodes = (this.items || [])
            .map(item => extractDialCode(item))
            .filter((dialCode): dialCode is string => !!dialCode);
        const parsedPhone = parseStoredPhone(value, availableDialCodes);

        if (parsedPhone.dialCode) {
            this.filteredCountries.forEach((element) => {
                const code = '+' + element.label.split('+')[1];
                if (code === parsedPhone.dialCode) {
                    this.defaultValue = element;
                    this.formGroup.get('new.code').setValue(element);
                }
            });
        } else {
            this.defaultValue = null;
            this.formGroup.get('new.code').setValue(null);
        }

        this.formGroup.get('new.numTel').setValue(parsedPhone.number);
        // this.getDisplayPhone();
    }

    changeSelect(event) {
        // les valeurs
        this.selected = event.currentTarget.value;
        const item = this.items.find((i) => i.value === this.selected);
        this.country = this.countries.find((c) => c.iso === this.selected);
        this.updateValue();
        // autosize
    }

    updateInput(event) {
        let newValue = '';
        const codeControl = this.formGroup.get('new.code');
        const countryCode = this.resolveDialCodeFromSelection(codeControl?.value);

        if (event && event.trim() !== '') {
            if (countryCode) {
                newValue = countryCode + '-' + event;
            } else {
                newValue = event;
            }
        }

        this.formGroup.get('value').setValue(newValue, { emitEvent: false });
    }

    updateValue() {
        let newValue: string;
        if (this.valueInput && this.valueInput.length > 0) {
            newValue = '+' + this.defaultValue.label.split('+')[1] + '-' + this.valueInput;
        } else {
            newValue = '';
        }

        if( this.value !== newValue){
            this.value = newValue;
            // this.getDisplayPhone();
            this.formGroup.get('value').setValue(this.value,{ emitEvent: false });
        }
        
    }

    parseCodeValue(value: string) {
        if (value) {
            value = value.replace(/\s/g, '');
            if (value.includes('+')) {
                value = '+' + value.split('+')[1];
                return value;
            }
        }
    }

    numberOnly(event): boolean {
        const charCode = event.which ? event.which : event.keyCode;
        if (this.valueInput && this.valueInput.length === 0 && charCode === 48) {
            return false;
        }
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            return false;
        }
        return true;
    }

    getTextWidth(text, font): number {
        const canvas =
            this.canvas || (this.canvas = document.createElement('canvas'));
        const context = canvas.getContext('2d');
        context.font = font;
        const metrics = context.measureText(text);
        return metrics.width;
    }

    filterCountry(event) {
        // in a real application,
        // make a request to a remote url with the query and return filtered results, for demo we filter at client side
        const filtered: any[] = [];
        const query = event.query;

        if (this.countries) {
            this.countries.forEach(country => {
                if (country.name.toLowerCase().indexOf(query.toLowerCase()) === 0) {
                    filtered.push(country);
                }
            });
        }


        this.filteredCountries = filtered;
    }

    selectItem(event) {
        const selectedValue = event?.value ?? event?.item ?? event;
        if (selectedValue?.value === this.separatorItem.value || selectedValue === this.separatorItem.value) {
            return;
        }
        const newCode = this.resolveDialCodeFromSelection(selectedValue);
        let newValue: string;
        const currentNumTel = this.formGroup.get('new.numTel')?.value;

        if (!newCode) {
            return;
        }

        // PrimeNG réduit la valeur sélectionnée à option.value ; on restaure l'objet complet.
        this.formGroup.get('new.code').setValue(selectedValue, { emitEvent: false });

        if (currentNumTel && currentNumTel.trim() !== '') {
            newValue = newCode + '-' + currentNumTel;
        } else {
            newValue = newCode + '-';
        }

        this.formGroup.get('value').setValue(newValue, { emitEvent: false });
        
    }

    search(event) {
        const filtered: any[] = [];
        const query = event.query;
        if (this.items) {
            this.items.forEach(country => {
                if (country.label.toLowerCase().indexOf(query.toLowerCase()) === 0) {
                    filtered.push(country);
                }
            });
        }
        this.filteredCountries = this.buildSuggestions(filtered);
    }

    getDisplayPhone(): void {
        if (this.value && this.value.split('-').length === 2 && this.value.split('-')[0].length > 0 && this.value.split('-')[1].length > 0) {
            const phone = this.value.split('-');
            const FrTel = (tel, sep) => tel.replace(/(\d\d(?!$))/g, '$1' + sep);
            this.displayPhone = '(' + phone[0] + ') ' + FrTel(phone[1], ' ');
        } else {
            this.displayPhone = null;
        }
    }

/*    save(event) {
        // this.valueChange.emit(this.currentValue.key);
        this.valueEditorChange.emit({
            value: this.value,
            status: true});
        this.valueChange.emit(this.value);
        this.focusOut(event);
    }*/

}
